<template>
	<div class="faq-page">
		<div class="faq-container">
			<!-- FAQ List (Left) -->
			<AdminListSection
				title="FAQs"
				:items="faqs"
				:selected-id="selectedFaq?.id"
				:loading="faqLoading"
				empty-message="No FAQs yet"
				:search-fields="['faqName']"
				:show-new-button="false"
				new-button-text="New FAQ"
				@select="selectFaq"
				@refresh="refreshFaqs"
				@new="newFaq"
			>
				<template #item="{ item }">
					<div class="item-info">
						<div class="item-name">{{ item.faqName }}</div>
					</div>
				</template>
			</AdminListSection>

			<!-- FAQ Items (Right) -->
			<div class="faq-items-section">
				<div v-if="selectedFaq">
					<div class="section-header">
						<h2>{{ selectedFaq.faqName }}</h2>
						<div class="header-actions-group">
							<button class="action-btn secondary" @click="toggleJsonEditor">
								{{ showJsonEditor ? 'Form View' : 'JSON View' }}
							</button>
							<AdminHeaderActions
								:loading="saving"
								:has-id="!!selectedFaq.id"
								@save="saveFaq"
								@delete="showDeleteFaqDialog = true"
								@new="newFaq"
							/>
						</div>
					</div>

					<!-- JSON Editor -->
					<div v-if="showJsonEditor" class="json-editor-section">
						<textarea
							v-model="jsonEditorContent"
							class="json-editor-textarea"
							spellcheck="false"
						></textarea>
						<p v-if="jsonError" class="json-error">{{ jsonError }}</p>
					</div>

					<admin-table
						v-else
						ref="faqItemsTable"
						title="Questions & Answers"
						icon="❓"
						:columns="faqItemColumns"
						:data="selectedFaq.faqItems || []"
						:loading="faqLoading"
						:searchable="true"
						search-placeholder="Search questions..."
						:collapsible="false"
						:default-expanded="true"
						:editable="true"
						:selectable="false"
						:deletable="true"
						:addable="true"
						empty-message="No questions yet. Add one below."
						@row-edit-save="handleItemEditSave"
						@row-delete="handleItemDelete"
						@row-add="handleItemAdd"
					>
						<template #cell-question="{ row }">
							<span class="cell-value">{{ row.question }}</span>
						</template>
						<template #cell-answer="{ row }">
							<span class="cell-value" :title="row.answer || ''">{{ truncateText(row.answer || '', 100) }}</span>
						</template>
					</admin-table>
				</div>
				<div v-else class="empty-state-section">
					<p>Select an FAQ to view questions</p>
				</div>
			</div>
		</div>
	</div>

	<!-- Add FAQ Modal -->
	<MGDialog
		v-model="showAddFaqModal"
		title="Add FAQ"
		max-width="400px"
		:persistent="true"
	>
		<template #content>
			<div class="form-group">
				<label>FAQ Name</label>
				<input v-model="newFaqName" type="text" class="form-input" placeholder="e.g., Money FAQs" />
			</div>
		</template>
		<template #actions>
			<MGButton text="Save" @click="addFaq" :stacked="true" />
			<MGButton text="Cancel" :outline="true" @click="showAddFaqModal = false" :stacked="true" />
		</template>
	</MGDialog>

	<!-- Delete FAQ Confirmation Dialog -->
	<MGDialog
		v-model="showDeleteFaqDialog"
		title="Delete FAQ"
		max-width="300px"
		:persistent="true"
	>
		<template #content>
			<p>Are you sure you want to delete "{{ selectedFaq?.faqName }}"? This will delete all questions in this FAQ.</p>
		</template>
		<template #actions>
			<MGButton text="Delete" @click="confirmDeleteFaq" :stacked="true" />
			<MGButton text="Cancel" :outline="true" @click="showDeleteFaqDialog = false" :stacked="true" />
		</template>
	</MGDialog>

	<!-- Delete Item Confirmation Dialog -->
	<MGDialog
		v-model="showDeleteItemDialog"
		title="Delete Question"
		max-width="300px"
		:persistent="true"
	>
		<template #content>
			<p>Are you sure you want to delete this question?</p>
		</template>
		<template #actions>
			<MGButton text="Delete" @click="confirmDeleteItem" :stacked="true" />
			<MGButton text="Cancel" :outline="true" @click="showDeleteItemDialog = false" :stacked="true" />
		</template>
	</MGDialog>

	<!-- Validation Toast -->
	<v-snackbar
		v-model="showValidationToast"
		:timeout="4000"
		location="bottom"
		color="error"
	>
		{{ validationMessage }}
		<template #actions>
			<v-btn
				icon="mdi-close"
				size="small"
				@click="showValidationToast = false"
			></v-btn>
		</template>
	</v-snackbar>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import MGDialog from '@/components/elements/MGDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';
import AdminTable from '@/admin/components/AdminTable.vue';
import AdminListSection from '@/admin/components/AdminListSection.vue';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';

export default {
	name: 'FaqPage',
	components: { MGDialog, MGButton, AdminTable, AdminListSection, AdminHeaderActions },
	mixins: [AdminServices],
	data() {
		return {
			selectedFaq: null,
			saving: false,
			showValidationToast: false,
			validationMessage: '',
			showAddFaqModal: false,
			showDeleteFaqDialog: false,
			showDeleteItemDialog: false,
			newFaqName: '',
			deleteItemId: null,
			showJsonEditor: false,
			jsonEditorContent: '',
			jsonError: '',
			faqItemColumns: [
				{
					field: 'question',
					label: 'Question',
					width: '2fr',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., How do I reset my password?'
				},
				{
					field: 'answer',
					label: 'Answer',
					width: '3fr',
					sortable: false,
					editable: true,
					type: 'textarea',
					required: true,
					placeholder: 'e.g., Click on "Forgot Password" on the login page...'
				}
			]
		};
	},
	watch: {
		// Keep selectedFaq in sync when faqs list updates
		faqs: {
			handler(newFaqs) {
				if (this.selectedFaq && newFaqs) {
					this.selectedFaq = newFaqs.find(f => f.id === this.selectedFaq.id) || null;
				}
			},
			deep: true
		}
	},
	methods: {
		truncateText(text, length) {
			if (!text) return '—';
			return text.length > length ? text.substring(0, length) + '...' : text;
		},
		selectFaq(faq) {
			this.selectedFaq = faq;
			this.showJsonEditor = false;
			this.jsonEditorContent = '';
			this.jsonError = '';
		},
		refreshFaqs() {
			this._getFaqs();
		},
		newFaq() {
			this.showAddFaqModal = true;
		},
		saveFaq() {
			let faqToSave = this.selectedFaq;

			// Apply JSON changes first if in JSON editor mode
			if (this.showJsonEditor) {
				try {
					faqToSave = JSON.parse(this.jsonEditorContent);
					this.jsonError = '';
				} catch (e) {
					this.jsonError = 'Invalid JSON: ' + e.message;
					return;
				}
			}

			if (!faqToSave) return;
			this.saving = true;
			this._updateFaq(faqToSave.id, faqToSave, () => {
				this.saving = false;
				// Update selectedFaq with the saved data
				this.selectedFaq = faqToSave;
			});
		},
		toggleJsonEditor() {
			this.jsonError = '';
			if (this.showJsonEditor) {
				// Switching to form view - apply JSON changes
				try {
					const parsed = JSON.parse(this.jsonEditorContent);
					this.selectedFaq = parsed;
					this.showJsonEditor = false;
				} catch (e) {
					this.jsonError = 'Invalid JSON: ' + e.message;
				}
			} else {
				// Switching to JSON view
				this.jsonEditorContent = JSON.stringify(this.selectedFaq, null, 2);
				this.showJsonEditor = true;
			}
		},
		applyJsonToFaq() {
			try {
				const parsed = JSON.parse(this.jsonEditorContent);
				this.selectedFaq = parsed;
				this.jsonError = '';
				return true;
			} catch (e) {
				this.jsonError = 'Invalid JSON: ' + e.message;
				return false;
			}
		},
		addFaq() {
			if (!this.newFaqName.trim()) {
				this.validationMessage = 'Please enter a FAQ name';
				this.showValidationToast = true;
				return;
			}
			this._createFaq({ faqName: this.newFaqName }, (result) => {
				this.faqs.push(result);
				this.selectFaq(result);
				this.showAddFaqModal = false;
				this.newFaqName = '';
			});
		},
		confirmDeleteFaq() {
			this._deleteFaq(this.selectedFaq.id, () => {
				this.faqs = this.faqs.filter(f => f.id !== this.selectedFaq.id);
				this.selectedFaq = this.faqs.length > 0 ? this.faqs[0] : null;
			});
			this.showDeleteFaqDialog = false;
		},
		handleItemEditSave({ rowId, data }) {
			if (!data.question || !data.answer) {
				const missing = [];
				if (!data.question) missing.push('Question');
				if (!data.answer) missing.push('Answer');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}
			// Update the item in the faqItems array
			const items = this.selectedFaq.faqItems || [];
			const index = items.findIndex(item => item.id === rowId);
			if (index !== -1) {
				items[index] = { ...items[index], question: data.question, answer: data.answer };
				this._updateFaq(this.selectedFaq.id, this.selectedFaq);
			}
		},
		handleItemDelete(row) {
			this.deleteItemId = row.id;
			this.showDeleteItemDialog = true;
		},
		confirmDeleteItem() {
			// Remove the item from the faqItems array
			this.selectedFaq.faqItems = (this.selectedFaq.faqItems || []).filter(item => item.id !== this.deleteItemId);
			this._updateFaq(this.selectedFaq.id, this.selectedFaq);
			this.showDeleteItemDialog = false;
			this.deleteItemId = null;
		},
		handleItemAdd(data) {
			if (!data.question || !data.answer) {
				const missing = [];
				if (!data.question) missing.push('Question');
				if (!data.answer) missing.push('Answer');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}
			// Add new item to the faqItems array
			if (!this.selectedFaq.faqItems) {
				this.selectedFaq.faqItems = [];
			}
			this.selectedFaq.faqItems.push({
				question: data.question,
				answer: data.answer,
				faqId: this.selectedFaq.id
			});
			this._updateFaq(this.selectedFaq.id, this.selectedFaq, () => {
				this.$refs.faqItemsTable?.resetAddRow();
			});
		}
	},
	mounted() {
		this._getFaqs();
	}
};
</script>

<style scoped>
.faq-page {
	min-height: 100%;
	padding: 15px;
	overflow-x: hidden;
}

.faq-container {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 15px;
}

.faq-items-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 12px;
	box-shadow: var(--box-shadow);
	max-height: 80vh;
	overflow-y: auto;
	min-width: 0;
}

.empty-state {
	text-align: center;
	padding: 40px 20px;
	color: var(--admin-text-secondary);
}

.empty-state-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 40px 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--admin-text-secondary);
	text-align: center;
	min-height: 400px;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 12px;
}

.section-header h2 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: bold;
}

.header-actions-group {
	display: flex;
	gap: 8px;
	align-items: center;
}

.action-btn {
	padding: 8px 16px;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s;
}

.action-btn.secondary {
	background: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.secondary:hover {
	background: var(--admin-active-bg);
}

/* JSON Editor */
.json-editor-section {
	background: var(--admin-hover-bg);
	border-radius: 8px;
	padding: 16px;
}

.json-editor-textarea {
	width: 100%;
	min-height: 500px;
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: monospace;
	resize: vertical;
	white-space: pre;
	overflow-x: auto;
}

.json-error {
	color: #f44336;
	font-size: 13px;
	margin: 8px 0 0 0;
}

.form-group {
	margin-bottom: 15px;
}

.form-group label {
	display: block;
	margin-bottom: 6px;
	color: var(--admin-text-primary);
	font-size: 12px;
	font-weight: bold;
}

.form-input {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.form-input:focus {
	outline: none;
	border-color: var(--admin-text-primary);
}

/* Cell styling */
.cell-value {
	color: var(--admin-text-primary);
	font-size: 14px;
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	transition: color 0.3s ease;
}

/* Snackbar positioning */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}

@media (max-width: 768px) {
	.faq-page {
		padding: 12px;
	}

	.faq-container {
		grid-template-columns: 1fr;
		gap: 12px;
	}

	.faq-items-section {
		padding: 15px;
	}
}

:root.light-mode .faq-items-section,
:root.light-mode .empty-state-section {
	background-color: #ffffff !important;
	border-color: #e0e0e0 !important;
}
</style>
